/**
 * Environment and Tenant Branding Configuration - PL-02
 *
 * Provides visual indicators for deployment environments (dev/staging/prod)
 * and per-tenant branding configuration.
 */

export type Environment = 'local' | 'development' | 'staging' | 'production'

export interface EnvironmentConfig {
  /** Display name */
  label: string
  /** Short label for compact display */
  shortLabel: string
  /** Banner background colour class */
  bannerBg: string
  /** Banner text colour class */
  bannerText: string
  /** Badge variant classes */
  badgeBg: string
  badgeText: string
  /** Whether to show the environment banner */
  showBanner: boolean
}

export interface TenantBranding {
  /** Tenant display name */
  name: string
  /** Short name / abbreviation for logo */
  shortName: string
  /** Primary brand colour (hex) for the logo background */
  primaryColour: string
  /** Logo text colour */
  logoTextColour: string
}

/**
 * Environment-specific configuration.
 * Production hides the banner to keep the UI clean.
 */
export const ENVIRONMENT_CONFIG: Record<Environment, EnvironmentConfig> = {
  local: {
    label: 'Local',
    shortLabel: 'LOCAL',
    bannerBg: 'bg-gray-500',
    bannerText: 'text-white',
    badgeBg: 'bg-gray-100',
    badgeText: 'text-gray-800',
    showBanner: true,
  },
  development: {
    label: 'Development',
    shortLabel: 'DEV',
    bannerBg: 'bg-amber-500',
    bannerText: 'text-white',
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-800',
    showBanner: true,
  },
  staging: {
    label: 'Staging',
    shortLabel: 'STG',
    bannerBg: 'bg-blue-500',
    bannerText: 'text-white',
    badgeBg: 'bg-blue-100',
    badgeText: 'text-blue-800',
    showBanner: true,
  },
  production: {
    label: 'Production',
    shortLabel: 'PROD',
    bannerBg: 'bg-green-600',
    bannerText: 'text-white',
    badgeBg: 'bg-green-100',
    badgeText: 'text-green-800',
    showBanner: false,
  },
}

/**
 * Default tenant branding used when no tenant-specific config is available.
 */
export const DEFAULT_TENANT_BRANDING: TenantBranding = {
  name: 'Operational Resilience',
  shortName: 'OR',
  primaryColour: '#4f46e5', // primary-600
  logoTextColour: '#ffffff',
}

/**
 * Detect the current environment from environment variables.
 * Falls back to 'development' if not set.
 */
export function detectEnvironment(): Environment {
  const env = import.meta.env.VITE_APP_ENVIRONMENT || import.meta.env.MODE || 'development'

  switch (env.toLowerCase()) {
    case 'production':
    case 'prod':
      return 'production'
    case 'staging':
    case 'stg':
    case 'uat':
      return 'staging'
    case 'development':
    case 'dev':
      return 'development'
    default:
      return 'local'
  }
}

/**
 * Get the current environment configuration.
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  return ENVIRONMENT_CONFIG[detectEnvironment()]
}

/**
 * Get tenant branding. In a real multi-tenant setup this would
 * be fetched from the API based on the current tenant context.
 * For now, returns defaults or reads from env variables.
 */
export function getTenantBranding(): TenantBranding {
  const name = import.meta.env.VITE_TENANT_NAME
  const shortName = import.meta.env.VITE_TENANT_SHORT_NAME

  if (name && shortName) {
    return {
      name,
      shortName,
      primaryColour: import.meta.env.VITE_TENANT_PRIMARY_COLOUR || DEFAULT_TENANT_BRANDING.primaryColour,
      logoTextColour: import.meta.env.VITE_TENANT_LOGO_TEXT_COLOUR || DEFAULT_TENANT_BRANDING.logoTextColour,
    }
  }

  return DEFAULT_TENANT_BRANDING
}
